import ExcelJS from "exceljs";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import isGHMC, {
  formatDateTimeWithoutSec,
  formattedDate,
} from "../../../../Common/helper/helpar";
import { useNavigate } from "react-router-dom";

export const NoOfBinsInnerExport = ({ data, fromDate, toDate, date }) => {
  const navigate = useNavigate();
  // Helper function to format YYYYMMDD date string to DD-MM-YYYY
  const formatDateKey = (dateKey) => {
    if (!dateKey || dateKey.length !== 8) return dateKey;
    const year = dateKey.substring(0, 4);
    const month = dateKey.substring(4, 6);
    const day = dateKey.substring(6, 8);
    return `${day}-${month}-${year}`;
  };

  // Extract all date keys from data and sort them chronologically
  const getDateKeys = () => {
    if (!data || data.length === 0) {
      return [];
    }
    const dateKeysSet = new Set();
    data.forEach((item) => {
      Object.keys(item).forEach((key) => {
        // Check if key is a date (8 digits, numeric) - format: YYYYMMDD
        if (/^\d{8}$/.test(key)) {
          dateKeysSet.add(key);
        }
      });
    });
    // Sort dates chronologically (YYYYMMDD format sorts correctly as strings)
    return Array.from(dateKeysSet).sort();
  };

  const dateKeys = getDateKeys();

  // Filter out summary rows (RowType !== 0) and get only data rows
  const dataRows = data ? data.filter((row) => row.RowType === 0) : [];

  // Calculate percentage for each row (dates with value > 0 / total dates * 100)
  const calculateRowPercentage = (row) => {
    if (!dateKeys || dateKeys.length === 0) return 0;
    const datesWithValue = dateKeys.filter(
      (dateKey) => Number(row[dateKey] || 0) > 0,
    ).length;
    return Math.round((datesWithValue / dateKeys.length) * 100);
  };

  // Calculate summary rows
  const calculateSummaryRows = () => {
    if (!dataRows || dataRows.length === 0) {
      return {
        overallGpvAttended: {},
        uniqueGpvAttended: {},
        performanceAchieved: {},
        totalUniqueGpvAttended: 0,
        totalPerformanceAchieved: 0,
      };
    }

    const overallGpvAttended = {};
    const uniqueGpvAttended = {};
    const performanceAchieved = {};

    const totalRows = dataRows.length; // ex: 2610

    // ================= OVERALL =================
    dateKeys.forEach((dateKey) => {
      overallGpvAttended[dateKey] = dataRows.reduce((sum, row) => {
        return sum + Number(row[dateKey] || 0);
      }, 0);
    });

    // ================= UNIQUE + PERFORMANCE =================
    dateKeys.forEach((dateKey) => {
      // count zero rows
      const zeroCount = dataRows.filter(
        (row) => Number(row[dateKey] || 0) === 0,
      ).length;

      // unique attended = total - zeros
      const attendedCount = totalRows - zeroCount;

      uniqueGpvAttended[dateKey] = attendedCount;

      // ⭐ performance = attended / total
      // performanceAchieved[dateKey] =
      //   totalRows > 0
      //     ? Math.round((attendedCount / totalRows) * 100)
      //     : 0;

      performanceAchieved[dateKey] =
        totalRows > 0 ? ((attendedCount / totalRows) * 100).toFixed(2) : "0.00";
    });

    // ================= TOTAL UNIQUE =================
    let totalMissed = 0;

    dataRows.forEach((row) => {
      const attended = dateKeys.some(
        (dateKey) => Number(row[dateKey] || 0) > 0,
      );
      if (!attended) totalMissed++;
    });

    const totalUniqueGpvAttended = totalRows - totalMissed;

    // ⭐ TOTAL PERFORMANCE = totalUnique / totalRows
    const totalPerformanceAchieved =
      totalRows > 0
        ? Math.round((totalUniqueGpvAttended / totalRows) * 100)
        : 0;

    return {
      overallGpvAttended,
      uniqueGpvAttended,
      performanceAchieved,
      totalUniqueGpvAttended,
      totalPerformanceAchieved,
    };
  };

  const getAdjustedTotal = (dateKey, dataRows) => {
    let zeroCount = 0;

    dataRows.forEach((row) => {
      if (!row[dateKey] || Number(row[dateKey]) === 0) {
        zeroCount++;
      }
    });

    return dataRows.length - zeroCount;
  };

  const getPerformancePercentage = (dateKey, dataRows, summaryRows) => {
    const unique = summaryRows.uniqueGpvAttended[dateKey] || 0;
    const adjustedTotal = getAdjustedTotal(dateKey, dataRows);

    if (adjustedTotal === 0) return 0;

    return Math.round((unique / adjustedTotal) * 100);
  };

  const summaryRows = calculateSummaryRows();
  const isallDates = localStorage.getItem("innerBinReportAllDates");
  const downloadAsExcel = async () => {
    const workbook = new ExcelJS.Workbook();
    const worksheet = workbook.addWorksheet("Zone Wise Report");

    const currentDate = new Date();
    const formattedDateStr = `${currentDate.getDate()}-${currentDate.getMonth() + 1}-${currentDate.getFullYear()}`;
    const formattedTime = `${currentDate.getHours()}:${currentDate.getMinutes()}:${currentDate.getSeconds()}`;

    const headerText = `Zone Wise Report as on (${
      isallDates === "all"
        ? `${formattedDate(fromDate)} to ${formattedDate(toDate)}`
        : formattedDate(date)
    })`;

    worksheet.addRow([
      `Zone Wise Report Generated on: ${formattedDateStr}, ${formattedTime}`,
    ]);

    const totalColumns = 5 + dateKeys.length + 2;

    worksheet.mergeCells(1, 1, 1, totalColumns);
    worksheet.getCell(1, 1).font = { bold: true };
    worksheet.getCell(1, 1).alignment = { horizontal: "center" };

    worksheet.addRow([headerText]);
    worksheet.mergeCells(2, 1, 2, totalColumns);
    worksheet.getCell(2, 1).font = { bold: true, size: 14 };
    worksheet.getCell(2, 1).alignment = {
      horizontal: "center",
      vertical: "middle",
    };

    const headerRowData = [
      "S.No",
      "Zone",
      "Circle Number",
      "Circle Name",
      "GVP Name",
      ...dateKeys.map((dateKey) => formatDateKey(dateKey)),
      "No.of Days Attended",
      "%",
    ];

    const headerRow = worksheet.addRow(headerRowData);

    headerRow.eachCell((cell) => {
      cell.font = { bold: true };
      cell.alignment = {
        wrapText: true,
        vertical: "middle",
        horizontal: "center",
      };
      cell.fill = {
        type: "pattern",
        pattern: "solid",
        fgColor: { argb: "F6B1B1" },
      };
      cell.font.color = { argb: "FFFFFFFF" };
    });

    dataRows.forEach((row, index) => {
      const attendedDays = dateKeys.filter(
        (dateKey) => Number(row[dateKey] || 0) > 0,
      ).length;

      const adjustedTotals = dateKeys.length;

      const percentage =
        adjustedTotals === 0
          ? 0
          : Math.round((attendedDays / adjustedTotals) * 100);

      const rowData = [
        index + 1,
        row.ZoneName || "N/A",
        row.CircleId || "N/A",
        row.CircleName || "N/A",
        row.GvpName || "N/A",
        ...dateKeys.map((dateKey) => row[dateKey] || 0),
        attendedDays,
        `${percentage}%`,
      ];

      const dataRow = worksheet.addRow(rowData);
      dataRow.eachCell((cell) => {
        cell.alignment = {
          wrapText: true,
          vertical: "middle",
          horizontal: "center",
        };
      });
    });

    if (dataRows.length > 0) {
      const overallRow = worksheet.addRow([
        "",
        "",
        "",
        "",
        "Overall GVP Attended",
        ...dateKeys.map(
          (dateKey) => summaryRows.overallGpvAttended[dateKey] || 0,
        ),
        "",
        "-",
      ]);

      overallRow.eachCell((cell) => {
        cell.font = { bold: true };
        cell.alignment = {
          wrapText: true,
          vertical: "middle",
          horizontal: "center",
        };
        cell.fill = {
          type: "pattern",
          pattern: "solid",
          fgColor: { argb: "F6B1B1" },
        };
        cell.font.color = { argb: "FFFFFFFF" };
      });

      const uniqueRow = worksheet.addRow([
        "",
        "",
        "",
        "",
        "Unique GVP Attended",
        ...dateKeys.map(
          (dateKey) => summaryRows.uniqueGpvAttended[dateKey] || 0,
        ),
        summaryRows.totalUniqueGpvAttended || 0,
        "-",
      ]);

      uniqueRow.eachCell((cell) => {
        cell.font = { bold: true };
        cell.alignment = {
          wrapText: true,
          vertical: "middle",
          horizontal: "center",
        };
        cell.fill = {
          type: "pattern",
          pattern: "solid",
          fgColor: { argb: "F6B1B1" },
        };
        cell.font.color = { argb: "FFFFFFFF" };
      });

      const performanceRow = worksheet.addRow([
        "",
        "",
        "",
        "",
        "Performance Achieved",
        ...dateKeys.map(
          (dateKey) => `${summaryRows.performanceAchieved?.[dateKey] || 0}%`,
        ),
        `${summaryRows.totalPerformanceAchieved || 0}%`,
        "-",
      ]);

      performanceRow.eachCell((cell) => {
        cell.font = { bold: true };
        cell.alignment = {
          wrapText: true,
          vertical: "middle",
          horizontal: "center",
        };
        cell.fill = {
          type: "pattern",
          pattern: "solid",
          fgColor: { argb: "FF0D5DA5" },
        };
        cell.font.color = { argb: "FFFFFFFF" };
      });
    }

    worksheet.columns = [
      { width: 8 },
      { width: 15 },
      { width: 15 },
      { width: 20 },
      { width: 25 },
      ...dateKeys.map(() => ({ width: 12 })),
      { width: 18 },
      { width: 10 },
    ];

    const buffer = await workbook.xlsx.writeBuffer();
    const blob = new Blob([buffer], {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    });

    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "ZoneWiseReport.xlsx";
    a.click();
    URL.revokeObjectURL(url);
  };

  /* ===================== PDF DOWNLOAD ===================== */
//  const downloadAsPDF = () => {
//   const doc = new jsPDF("l", "mm", "a4");

//   const currentDate = new Date();
//   const formattedDateStr = `${currentDate.getDate()}-${currentDate.getMonth() + 1}-${currentDate.getFullYear()}`;
//   const formattedTime = `${currentDate.getHours()}:${currentDate.getMinutes()}:${currentDate.getSeconds()}`;

//   const headerText = `Zone Wise Report as on (${
//     isallDates === "all"
//       ? `${formattedDate(fromDate)} to ${formattedDate(toDate)}`
//       : formattedDate(date)
//   })`;

//   // Report Generated Time
//   doc.setFontSize(10);
//   doc.text(
//     `Zone Wise Report Generated on: ${formattedDateStr}, ${formattedTime}`,
//     210,
//     10,
//     { align: "center" }
//   );

//   // Main Title
//   doc.setFontSize(14);
//   doc.text(headerText, 210, 18, { align: "center" });

//   const tableColumns = [
//     "S.No",
//     "Zone",
//     "Circle Number",
//     "Circle Name",
//     "GVP Name",
//     ...dateKeys.map((dateKey) => formatDateKey(dateKey)),
//     "No.of Days Attended",
//     "%",
//   ];

//   const tableRows = dataRows.map((row, index) => [
//     index + 1,
//     row.ZoneName || "N/A",
//     row.CircleId || "N/A",
//     row.CircleName || "N/A",
//     row.GvpName || "N/A",
//     ...dateKeys.map((dateKey) => row[dateKey] || 0),
//     row.NoOfDays || 0,
//     `${calculateRowPercentage(row)}%`,
//   ]);

//   // Summary Rows
//   if (dataRows.length > 0) {
//     tableRows.push([
//       "",
//       "",
//       "",
//       "",
//       "Overall GVP Attended",
//       ...dateKeys.map(
//         (dateKey) => summaryRows.overallGpvAttended?.[dateKey] || 0
//       ),
//       "",
//       "-",
//     ]);

//     tableRows.push([
//       "",
//       "",
//       "",
//       "",
//       "Unique GVP Attended",
//       ...dateKeys.map(
//         (dateKey) => summaryRows.uniqueGpvAttended?.[dateKey] || 0
//       ),
//       summaryRows.totalUniqueGpvAttended || 0,
//       "-",
//     ]);

//     tableRows.push([
//       "",
//       "",
//       "",
//       "",
//       "Performance Achieved",
//       ...dateKeys.map(
//         (dateKey) => `${summaryRows.performanceAchieved?.[dateKey] || 0}%`
//       ),
//       `${summaryRows.totalPerformanceAchieved || 0}%`,
//       "-",
//     ]);
//   }

//   autoTable(doc, {
//     head: [tableColumns],
//     body: tableRows,
//     startY: 25,
//     tableWidth: "auto",
//     styles: {
//       fontSize: 7,
//       cellPadding: 2,
//       halign: "center",
//       valign: "middle",
//     },

//     headStyles: {
//       fillColor: isGHMC() ? [13, 93, 165] : [236, 17, 23],
//       textColor: [255, 255, 255],
//       fontStyle: "bold",
//     },

//     // Dynamic column widths
//     columnStyles: {
//       0: { cellWidth: 12 }, // S.No
//       1: { cellWidth: 25 }, // Zone
//       2: { cellWidth: 20 }, // Circle Number
//       3: { cellWidth: 35 }, // Circle Name
//       4: { cellWidth: 50 }, // GVP Name
//     },

//     didParseCell: function (data) {
//       const rowIndex = data.row.index;
//       const row = tableRows[rowIndex];

//       if (!row) return;

//       if (row[4] === "Overall GVP Attended") {
//         data.cell.styles.fillColor = [246, 177, 177];
//         data.cell.styles.textColor = [255, 255, 255];
//         data.cell.styles.fontStyle = "bold";
//       }

//       if (row[4] === "Unique GVP Attended") {
//         data.cell.styles.fillColor = [246, 177, 177];
//         data.cell.styles.textColor = [255, 255, 255];
//         data.cell.styles.fontStyle = "bold";
//       }

//       if (row[4] === "Performance Achieved") {
//         data.cell.styles.fillColor = [13, 93, 165];
//         data.cell.styles.textColor = [255, 255, 255];
//         data.cell.styles.fontStyle = "bold";
//       }
//     },
//   });

//   doc.save("ZoneWiseReport.pdf");
// };


const downloadAsPDF = () => {
  const doc = new jsPDF("l", "mm", "a4");

  const currentDate = new Date();
  const formattedDateStr = `${currentDate.getDate()}-${currentDate.getMonth() + 1}-${currentDate.getFullYear()}`;
  const formattedTime = `${currentDate.getHours()}:${currentDate.getMinutes()}:${currentDate.getSeconds()}`;

  const headerText = `Zone Wise Report as on (${
    isallDates === "all"
      ? `${formattedDate(fromDate)} to ${formattedDate(toDate)}`
      : formattedDate(date)
  })`;

  doc.setFontSize(10);
  doc.text(
    `Zone Wise Report Generated on: ${formattedDateStr}, ${formattedTime}`,
    148,
    10,
    { align: "center" }
  );

  doc.setFontSize(14);
  doc.text(headerText, 148, 18, { align: "center" });

  const tableColumns = [
    "S.No",
    "Zone",
    "Circle Number",
    "Circle Name",
    "GVP Name",
    ...dateKeys.map((dateKey) => formatDateKey(dateKey)),
    "No.of Days Attended",
    "%",
  ];

  const tableRows = dataRows.map((row, index) => [
    index + 1,
    row.ZoneName || "N/A",
    row.CircleId || "N/A",
    row.CircleName || "N/A",
    row.GvpName || "N/A",
    ...dateKeys.map((dateKey) => row[dateKey] || 0),
    row.NoOfDays || 0,
    `${calculateRowPercentage(row)}%`,
  ]);

  if (dataRows.length > 0) {
    tableRows.push([
      "",
      "",
      "",
      "",
      "Overall GVP Attended",
      ...dateKeys.map(
        (dateKey) => summaryRows.overallGpvAttended?.[dateKey] || 0
      ),
      "",
      "-",
    ]);

    tableRows.push([
      "",
      "",
      "",
      "",
      "Unique GVP Attended",
      ...dateKeys.map(
        (dateKey) => summaryRows.uniqueGpvAttended?.[dateKey] || 0
      ),
      summaryRows.totalUniqueGpvAttended || 0,
      "-",
    ]);

    tableRows.push([
      "",
      "",
      "",
      "",
      "Performance Achieved",
      ...dateKeys.map(
        (dateKey) => `${summaryRows.performanceAchieved?.[dateKey] || 0}%`
      ),
      `${summaryRows.totalPerformanceAchieved || 0}%`,
      "-",
    ]);
  }

  // Page width
  const pageWidth = doc.internal.pageSize.getWidth();

  // Fixed columns width
  const fixedWidth =
    10 + // S.No
    25 + // Zone
    15 + // Circle Number
    35 + // Circle Name
    40 + // GVP Name
    25 + // No.of Days
    12; // %

  // Remaining width for date columns
  const remainingWidth = pageWidth - fixedWidth - 20;

  const dateColumnWidth = remainingWidth / dateKeys.length;

  const columnStyles = {
  0: { cellWidth: 10 },  // S.No
  1: { cellWidth: 25 }, // Zone
  2: { cellWidth: 15 }, // Circle Number
  3: { cellWidth: 35 }, // Circle Name
  4: { cellWidth: 40 }, // GVP Name
};

dateKeys.forEach((_, index) => {
  columnStyles[index + 5] = { cellWidth: dateColumnWidth };
});

columnStyles[5 + dateKeys.length] = { cellWidth: 25 }; // No.of Days
columnStyles[6 + dateKeys.length] = { cellWidth: 12 };  // %

  autoTable(doc, {
    head: [tableColumns],
    body: tableRows,
    startY: 25,
    styles: {
      fontSize: 7,
      cellPadding: 2,
      halign: "center",
      valign: "middle",
    },
    columnStyles: columnStyles,

    headStyles: {
      fillColor: isGHMC() ? [13, 93, 165] : [236, 17, 23],
      textColor: [255, 255, 255],
      fontStyle: "bold",
    },

    didParseCell: function (data) {
      const rowIndex = data.row.index;
      const row = tableRows[rowIndex];

      if (!row) return;

      if (row[4] === "Overall GVP Attended") {
        data.cell.styles.fillColor = [246, 177, 177];
        data.cell.styles.textColor = [255, 255, 255];
        data.cell.styles.fontStyle = "bold";
      }

      if (row[4] === "Unique GVP Attended") {
        data.cell.styles.fillColor = [246, 177, 177];
        data.cell.styles.textColor = [255, 255, 255];
        data.cell.styles.fontStyle = "bold";
      }

      if (row[4] === "Performance Achieved") {
        data.cell.styles.fillColor = [13, 93, 165];
        data.cell.styles.textColor = [255, 255, 255];
        data.cell.styles.fontStyle = "bold";
      }
    },
  });

  doc.save("ZoneWiseReport.pdf");
};


  /* ===================== UI ===================== */
  return (
    <div className="d-flex justify-content-end mt-3">
      <button className="btn btn-secondary" onClick={() => navigate(-1)}>
        Back
      </button>
      <button className="btn btn-excel me-2" onClick={downloadAsPDF}>
        <i className="fa-solid fa-file-pdf me-1"></i> PDF
      </button>

      <button className="btn btn-excel" onClick={downloadAsExcel}>
        <i className="fa-solid fa-file-excel me-1"></i> Excel
      </button>
    </div>
  );
};
